import { useEffect, useState } from 'react'

import benefitPlanPeriods, { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import { getPerksEEDashboardSummary } from 'core/application/employeeDashboard/perks/getPerksEEDashboardSummary'
import {
  GetBenefitUsageDetailsRequestsResponse,
  getBenefitUsageDetails,
} from 'core/application/employeeDashboard/reimbursements/perks/getBenefitUsageDetails'

import { Col, Row, Skeleton } from 'common/components'

import SummaryTitle from '../../../adminDashboard/benefitDetails/summary/SummaryTitle'
import { BenefitPlanPeriodDropDown, Card } from '../../../common/components'
import { useQueryRequestV2 } from '../../../common/hooks'
import PerksAllowance from './perksAllowance'
import PerksEmployeeReimbursementRequests from './reimbursementRequests'
import TotalAllowanceStatistic from './totalAllowanceStatistic'
import TotalExpenseStatistic from './totalExpenseStatistic'

const PerksEmployeeDashboardHome = () => {
  const { data: planPeriods } = useQueryRequestV2<PlanPeriodDto[]>({
    initialState: null,
    queryFn: () => benefitPlanPeriods(),
  })

  const [planPeriod, setPlanPeriod] = useState<PlanPeriodDto | null>(null)

  const {
    data: categoriesSummary,
    reload: reloadCategoriesSummary,
    isLoading: categorySummaryLoading,
  } = useQueryRequestV2({
    initialState: { isEligible: true, offerSameAllowance: false, categorySummaries: [] },
    queryFn: () => getPerksEEDashboardSummary(planPeriod?.id),
    enable: !!planPeriod?.id,
  })

  useEffect(() => void reloadCategoriesSummary(), [planPeriod])

  useEffect(() => {
    if (planPeriods && planPeriods.length > 0) {
      const found = planPeriods.find((x) => x.active) ? planPeriods.find((x) => x.active) : planPeriods[0]

      if (found) {
        setPlanPeriod(found)
      }
    }
  }, [planPeriods])

  const onPlanPeriodChanged = (planPeriodId: string) => {
    setPlanPeriod(planPeriods.find((x) => x.id === planPeriodId))
  }

  return (
    <Card
      title={
        <Row justify="space-between" className="w-100">
          <Col>
            <SummaryTitle />
          </Col>
          <Col>
            {planPeriods && (
              <BenefitPlanPeriodDropDown
                text="Benefit Period"
                defaultValue={planPeriod?.id}
                planPeriods={planPeriods}
                onPlanPeriodSelected={onPlanPeriodChanged}
                showStartDateAndEndDate={true}
              />
            )}
          </Col>
        </Row>
      }
      className="benefit_details__summary"
    >
      <Skeleton active loading={!planPeriod}>
        <>
          {planPeriod && (
            <>
              <PerksAllowance
                planPeriodId={planPeriod.id}
                endDate={planPeriod.endDate}
                categoriesSummary={categoriesSummary}
                categorySummaryLoading={categorySummaryLoading}
              />
              <PerksEmployeeReimbursementRequests planPeriodId={planPeriod.id} />
            </>
          )}
          <Row gutter={{ xl: 15 }}>{planPeriod?.id && <BenefitDetails planPeriod={planPeriod} />}</Row>
        </>
      </Skeleton>
    </Card>
  )
}

export default PerksEmployeeDashboardHome

type BenefitDetailsProps = {
  planPeriod: PlanPeriodDto
}

const BenefitDetails = ({ planPeriod }: BenefitDetailsProps) => {
  const {
    data: stats,
    reload: reloadStats,
    isLoading: areStatsLoading,
  } = useQueryRequestV2<GetBenefitUsageDetailsRequestsResponse>({
    initialState: {
      totalUsage: {
        id: 0,
        categoryName: '',
        totalAllowance: 0,
        allowanceUsed: 0,
        availableAllowance: 0,
        percentageIncrease: 0,
        percentageUsed: 0,
        color: '',
      },
      categoryUsages: [],
      offerSameAllowance: false,
    },
    queryFn: () => getBenefitUsageDetails(planPeriod.id),
  })

  useEffect(() => {
    reloadStats()
  }, [planPeriod?.id])
  const offerAllowance = stats?.categoryUsages[0]
    ? stats?.categoryUsages[0].offerSameAllowance
    : stats.offerSameAllowance

  return (
    <Skeleton loading={areStatsLoading}>
      {stats?.categoryUsages && offerAllowance ? (
        <Col span={24} xl={19}>
          {stats?.totalUsage && (
            <TotalAllowanceStatistic data={stats.totalUsage} offerSameAllowance={stats.offerSameAllowance} />
          )}
        </Col>
      ) : (
        <Col span={24} xl={9}>
          {stats?.totalUsage && (
            <TotalAllowanceStatistic data={stats.totalUsage} offerSameAllowance={stats.offerSameAllowance} />
          )}
        </Col>
      )}
      <Col span={24} xl={15}>
        {stats?.categoryUsages && (
          <TotalExpenseStatistic data={stats.categoryUsages} offerSameAllowance={offerAllowance} />
        )}
      </Col>
    </Skeleton>
  )
}
